import { useState, useEffect, useMemo } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { 
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { 
  ShoppingBag, 
  Plus, 
  Search, 
  CalendarIcon,
  DollarSign,
  Package,
  ArrowRightLeft,
  ArrowDown,
  ArrowUp,
  Loader2,
  Edit,
  Trash2,
  UserPlus,
  X,
  ChevronDown
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import type { Database } from '@/integrations/supabase/types';
import { productService } from '@/lib/packages';

type Bayi = {
  id: string;
  name: string;
  contact_person: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  color: string;
  notes: string | null;
  is_active: boolean;
  created_at: string;
};

type Trade = {
  id: string;
  bayi_id: string;
  trade_date: string;
  trade_type: 'exchange' | 'we_give' | 'we_receive';
  settlement_type: 'cash' | 'same_product' | 'replacement_product';
  settlement_amount: number;
  settlement_product_id: string | null;
  settlement_quantity: number;
  status: 'pending' | 'completed' | 'cancelled';
  notes: string | null;
  created_at: string;
  bayi?: Bayi;
  trade_items?: TradeItem[];
};

type TradeItem = {
  id: string;
  product_id: string;
  quantity: number;
  direction: 'in' | 'out';
  unit_price: number | null;
  total_price: number | null;
  status?: 'pending' | 'completed';
  product?: {
    name: string;
    item_code: string;
    current_cost: number | null;
  };
};

type Product = Database['public']['Tables']['products']['Row'];

const formatPrice = (price: number) => `₺${price.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const tradeTypeLabels = {
  we_give: { 
    label: 'Biz Veriyoruz', 
    labelPast: 'Biz Verdik',
    icon: ArrowDown, 
    color: 'bg-red-500/20 text-red-600' 
  },
  we_receive: { 
    label: 'Biz Alıyoruz', 
    labelPast: 'Biz Aldık',
    icon: ArrowUp, 
    color: 'bg-green-500/20 text-green-600' 
  }
};

const settlementTypeLabels = {
  cash: { label: 'Nakit', icon: DollarSign },
  same_product: { label: 'Aynı Ürün', icon: Package },
  replacement_product: { label: 'Değişim Ürünü', icon: ArrowRightLeft }
};

const statusLabels = {
  pending: { label: 'Açık', color: 'bg-yellow-500/20 text-yellow-600' },
  completed: { label: 'Kapatıldı', color: 'bg-green-500/20 text-green-600' },
  cancelled: { label: 'İptal', color: 'bg-red-500/20 text-red-600' }
};

// Distinctive colors for Bayis (different from customer colors: blue, red, green, yellow, black, gold)
const bayiColorOptions = [
  { value: '#8B5CF6', label: 'Mor' },
  { value: '#A855F7', label: 'Violet' },
  { value: '#6366F1', label: 'İndigo' },
  { value: '#EC4899', label: 'Pembe' },
  { value: '#F97316', label: 'Turuncu' },
  { value: '#14B8A6', label: 'Teal' },
  { value: '#06B6D4', label: 'Cyan' },
  { value: '#9333EA', label: 'Koyu Mor' }
];

export default function BayiTrades() {
  const { companyId, user } = useAuth();
  const { toast } = useToast();
  
  const [bayis, setBayis] = useState<Bayi[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBayi, setSelectedBayi] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  
  // Modal states
  const [showNewBayiModal, setShowNewBayiModal] = useState(false);
  const [showNewTradeModal, setShowNewTradeModal] = useState(false);
  const [showCloseTradeModal, setShowCloseTradeModal] = useState(false);
  const [tradeToClose, setTradeToClose] = useState<Trade | null>(null);
  
  // Close trade form
  const [closeTradeForm, setCloseTradeForm] = useState({
    settlement_type: 'cash' as 'cash' | 'same_product' | 'replacement_product',
    settlement_amount: 0,
    settlement_product_id: '',
    settlement_quantity: 1,
    selectedItemIds: [] as string[] // Track which items to close
  });
  
  // New Bayi form
  const [newBayi, setNewBayi] = useState({
    name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    color: '#8B5CF6', // Default purple color (distinctive from customer colors)
    notes: ''
  });
  
  // New Trade form (settlement will be set when closing)
  const [newTrade, setNewTrade] = useState({
    bayi_id: '',
    trade_date: new Date(),
    trade_type: 'we_give' as 'we_give' | 'we_receive',
    notes: '',
    items: [] as Array<{ product_id: string; quantity: number; direction: 'in' | 'out'; unit_price: number }>
  });

  // Product search states for each item
  const [productSearchTerms, setProductSearchTerms] = useState<Record<number, string>>({});
  const [productSearchResults, setProductSearchResults] = useState<Record<number, Product[]>>({});
  const [productSearchLoading, setProductSearchLoading] = useState<Record<number, boolean>>({});

  // Product search for settlement product
  const [settlementProductSearchTerm, setSettlementProductSearchTerm] = useState('');
  const [settlementProductSearchResults, setSettlementProductSearchResults] = useState<Product[]>([]);
  const [settlementProductSearchLoading, setSettlementProductSearchLoading] = useState(false);
  const [settlementProductSearchHasMore, setSettlementProductSearchHasMore] = useState(false);

  // Fetch data
  useEffect(() => {
    if (companyId) {
      fetchBayis();
      fetchTrades();
      fetchProducts();
    }
  }, [companyId]);

  const fetchBayis = async () => {
    if (!companyId) return;
    
    try {
      const { data, error } = await supabase
        .from('bayis')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');
      
      if (error) throw error;
      setBayis(data || []);
    } catch (error) {
      console.error('Error fetching bayis:', error);
      toast({
        title: 'Hata',
        description: 'Bayiler yüklenirken bir hata oluştu',
        variant: 'destructive'
      });
    }
  };

  const fetchTrades = async () => {
    if (!companyId) return;
    
    try {
      setLoading(true);
      
      // Fetch trades with related data (including product costs)
      let tradesData: any[] = [];
      let page = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data: pageData, error } = await supabase
          .from('trades')
          .select(`
            *,
            bayis(*),
            trade_items(
              *,
              products(name, item_code, current_cost)
            )
          `)
          .eq('company_id', companyId)
          .order('trade_date', { ascending: false })
          .range(page * pageSize, (page + 1) * pageSize - 1);
        
        if (error) throw error;

        if (pageData && pageData.length > 0) {
          tradesData = [...tradesData, ...pageData];
          page++;
          hasMore = pageData.length === pageSize;
        } else {
          hasMore = false;
        }
      }
      
      setTrades(tradesData || []);
      
      // Auto-fix trades that should be completed but aren't (runs in background)
      autoFixCompletedTrades(tradesData).then((fixedTradeIds) => {
        if (fixedTradeIds.length > 0) {
          // Update local state instead of refetching to avoid infinite loops
          setTrades(prevTrades => 
            prevTrades.map(trade => 
              fixedTradeIds.includes(trade.id) 
                ? { ...trade, status: 'completed' as const }
                : trade
            )
          );
        }
      });
    } catch (error) {
      console.error('Error fetching trades:', error);
      toast({
        title: 'Hata',
        description: 'Ticaret kayıtları yüklenirken bir hata oluştu',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Auto-fix trades that have all items completed but status is still pending
  const autoFixCompletedTrades = async (tradesData: any[]): Promise<string[]> => {
    if (!companyId) return [];

    try {
      const tradesToFix: string[] = [];
      
      // Check each trade to see if all items are completed but trade status is pending
      for (const trade of tradesData) {
        if (trade.status === 'pending') {
          const tradeItems = trade.trade_items || [];
          if (tradeItems.length > 0) {
            const allCompleted = tradeItems.every((item: any) => item.status === 'completed');
            if (allCompleted) {
              tradesToFix.push(trade.id);
            }
          }
        }
      }

      // Update trades that need fixing
      if (tradesToFix.length > 0) {
        const { error } = await supabase
          .from('trades')
          .update({ status: 'completed' })
          .in('id', tradesToFix)
          .eq('company_id', companyId);

        if (error) {
          console.error('Error auto-fixing completed trades:', error);
          return [];
        } else {
          console.log(`Auto-fixed ${tradesToFix.length} trade(s) that should be completed`);
          return tradesToFix;
        }
      }
      return [];
    } catch (error) {
      console.error('Error in autoFixCompletedTrades:', error);
      // Don't show error to user, just log it
      return [];
    }
  };

  const fetchProducts = async () => {
    if (!companyId) return;
    
    try {
      let productsData: Product[] = [];
      let page = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data: pageData, error } = await supabase
          .from('products')
          .select('*')
          .eq('company_id', companyId)
          .order('name')
          .range(page * pageSize, (page + 1) * pageSize - 1);
        
        if (error) throw error;

        if (pageData && pageData.length > 0) {
          productsData = [...productsData, ...pageData];
          page++;
          hasMore = pageData.length === pageSize;
        } else {
          hasMore = false;
        }
      }
      
      setProducts(productsData || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleCreateBayi = async () => {
    if (!companyId || !newBayi.name.trim()) {
      toast({
        title: 'Hata',
        description: 'Bayi adı zorunludur',
        variant: 'destructive'
      });
      return;
    }

    try {
      const { data, error } = await supabase
        .from('bayis')
        .insert({
          company_id: companyId,
          name: newBayi.name,
          contact_person: newBayi.contact_person || null,
          phone: newBayi.phone || null,
          email: newBayi.email || null,
          address: newBayi.address || null,
          color: newBayi.color,
          notes: newBayi.notes || null
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: 'Başarılı',
        description: 'Bayi başarıyla eklendi'
      });

      setNewBayi({
        name: '',
        contact_person: '',
        phone: '',
        email: '',
        address: '',
        color: '#3B82F6',
        notes: ''
      });
      setShowNewBayiModal(false);
      fetchBayis();
    } catch (error: any) {
      console.error('Error creating bayi:', error);
      toast({
        title: 'Hata',
        description: error.message || 'Bayi eklenirken bir hata oluştu',
        variant: 'destructive'
      });
    }
  };

  const handleCreateTrade = async () => {
    if (!companyId || !user) {
      toast({
        title: 'Hata',
        description: 'Oturum bilgisi bulunamadı',
        variant: 'destructive'
      });
      return;
    }

    if (!newTrade.bayi_id) {
      toast({
        title: 'Hata',
        description: 'Bayi seçimi zorunludur',
        variant: 'destructive'
      });
      return;
    }

    if (newTrade.items.length === 0) {
      toast({
        title: 'Hata',
        description: 'En az bir ürün eklemelisiniz',
        variant: 'destructive'
      });
      return;
    }

    try {
      // Create trade
      const { data: trade, error: tradeError } = await supabase
        .from('trades')
        .insert({
          company_id: companyId,
          bayi_id: newTrade.bayi_id,
          trade_date: format(newTrade.trade_date, 'yyyy-MM-dd'),
          trade_type: newTrade.trade_type,
          settlement_type: 'cash', // Default, will be updated when closing
          settlement_amount: 0, // Will be set when closing
          settlement_product_id: null, // Will be set when closing
          settlement_quantity: 0, // Will be set when closing
          notes: newTrade.notes || null,
          created_by: user.id,
          status: 'pending' // Start as open/pending
        })
        .select()
        .single();

      if (tradeError) throw tradeError;

      // Create trade items
      const tradeItems = newTrade.items.map(item => ({
        trade_id: trade.id,
        company_id: companyId,
        product_id: item.product_id,
        quantity: item.quantity,
        direction: item.direction,
        unit_price: item.unit_price,
        total_price: item.quantity * item.unit_price
      }));

      const { error: itemsError } = await supabase
        .from('trade_items')
        .insert(tradeItems);

      if (itemsError) throw itemsError;

      // Adjust inventory immediately based on trade type
      await handleInventoryAdjustmentsOnCreate(trade.id, newTrade);

      toast({
        title: 'Başarılı',
        description: 'Ticaret kaydı başarıyla oluşturuldu'
      });

      // Reset form
      setNewTrade({
        bayi_id: '',
        trade_date: new Date(),
        trade_type: 'we_give',
        notes: '',
        items: []
      });
      setProductSearchTerms({});
      setProductSearchResults({});
      setShowNewTradeModal(false);
      fetchTrades();
    } catch (error: any) {
      console.error('Error creating trade:', error);
      toast({
        title: 'Hata',
        description: error.message || 'Ticaret kaydı oluşturulurken bir hata oluştu',
        variant: 'destructive'
      });
    }
  };

  const handleInventoryAdjustments = async (tradeId: string, trade: typeof newTrade) => {
    if (!companyId) return;

    try {
      // Adjust inventory for trade items
      for (const item of trade.items) {
        const product = products.find(p => p.id === item.product_id);
        if (!product) continue;

        const adjustment = item.direction === 'out' ? -item.quantity : item.quantity;
        const newQuantity = product.quantity_on_hand + adjustment;

        // Update product inventory
        const { error: updateError } = await supabase
          .from('products')
          .update({ quantity_on_hand: newQuantity })
          .eq('id', item.product_id);

        if (updateError) throw updateError;

        // Create inventory movement
        await supabase
          .from('inventory_movements')
          .insert({
            company_id: companyId,
            product_id: item.product_id,
            movement_type: item.direction === 'out' ? 'adjustment_out' : 'purchase_in',
            qty: Math.abs(adjustment),
            related_table: 'trades',
            related_id: tradeId,
            actor_user_id: user?.id || null
          });
      }

      // Handle settlement inventory adjustments
      if (trade.settlement_type === 'same_product' || trade.settlement_type === 'replacement_product') {
        if (trade.settlement_product_id) {
          const settlementProduct = products.find(p => p.id === trade.settlement_product_id);
          if (settlementProduct) {
            // Settlement product is what we're receiving to close the trade
            // - we_give: We gave products, closing by receiving settlement product → inventory increases
            // - we_receive: We received products, closing by giving settlement product → inventory decreases
            // - exchange: We exchanged products, closing by receiving settlement product → inventory increases
            const settlementAdjustment = trade.trade_type === 'we_receive' 
              ? -trade.settlement_quantity 
              : trade.settlement_quantity;
            
            const newSettlementQuantity = settlementProduct.quantity_on_hand + settlementAdjustment;

            // Update settlement product inventory
            const { error: settlementUpdateError } = await supabase
              .from('products')
              .update({ quantity_on_hand: newSettlementQuantity })
              .eq('id', trade.settlement_product_id);

            if (settlementUpdateError) throw settlementUpdateError;

            // Create inventory movement for settlement
            await supabase
              .from('inventory_movements')
              .insert({
                company_id: companyId,
                product_id: trade.settlement_product_id,
                movement_type: settlementAdjustment > 0 ? 'purchase_in' : 'adjustment_out',
                qty: Math.abs(settlementAdjustment),
                related_table: 'trades',
                related_id: tradeId,
                actor_user_id: user?.id || null
              });
          }
        }
      }
    } catch (error) {
      console.error('Error adjusting inventory:', error);
      throw error;
    }
  };

  const addTradeItem = () => {
    setNewTrade(prev => ({
      ...prev,
      items: [...prev.items, { product_id: '', quantity: 1, direction: 'out', unit_price: 0 }]
    }));
  };

  const removeTradeItem = (index: number) => {
    setNewTrade(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateTradeItem = (index: number, field: string, value: any) => {
    setNewTrade(prev => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i === index) {
          const updated = { ...item, [field]: value };
          // Auto-fetch product cost when product is selected
          if (field === 'product_id' && value) {
            const product = products.find(p => p.id === value) || 
                           Object.values(productSearchResults).flat().find(p => p.id === value);
            if (product && product.current_cost) {
              updated.unit_price = product.current_cost;
            }
            // Clear search when product is selected
            setProductSearchTerms(prev => ({ ...prev, [index]: '' }));
            setProductSearchResults(prev => ({ ...prev, [index]: [] }));
          }
          return updated;
        }
        return item;
      })
    }));
  };

  // Search products for a specific item index
  const searchProductsForItem = async (index: number, searchTerm: string) => {
    if (!companyId || searchTerm.length < 2) {
      setProductSearchResults(prev => ({ ...prev, [index]: [] }));
      setProductSearchLoading(prev => ({ ...prev, [index]: false }));
      return;
    }

    setProductSearchLoading(prev => ({ ...prev, [index]: true }));
    try {
      const results = await productService.searchProducts(companyId, searchTerm, 20);
      setProductSearchResults(prev => ({ ...prev, [index]: results }));
    } catch (error) {
      console.error('Error searching products:', error);
      setProductSearchResults(prev => ({ ...prev, [index]: [] }));
    } finally {
      setProductSearchLoading(prev => ({ ...prev, [index]: false }));
    }
  };

  // Handle product search input change
  useEffect(() => {
    const timers: Record<number, NodeJS.Timeout> = {};
    
    Object.entries(productSearchTerms).forEach(([indexStr, term]) => {
      const index = parseInt(indexStr);
      if (timers[index]) {
        clearTimeout(timers[index]);
      }
      
      if (term && term.length >= 2) {
        timers[index] = setTimeout(() => {
          searchProductsForItem(index, term);
        }, 300);
      } else {
        setProductSearchResults(prev => ({ ...prev, [index]: [] }));
      }
    });

    return () => {
      Object.values(timers).forEach(timer => clearTimeout(timer));
    };
  }, [productSearchTerms, companyId]);

  const handleOpenCloseTradeModal = (trade: Trade) => {
    setTradeToClose(trade);
    // Pre-select all pending items
    const pendingItemIds = (trade.trade_items || [])
      .filter((item: TradeItem) => item.status !== 'completed')
      .map((item: TradeItem) => item.id);
    
    setCloseTradeForm({
      settlement_type: 'cash',
      settlement_amount: 0,
      settlement_product_id: '',
      settlement_quantity: 1,
      selectedItemIds: pendingItemIds
    });
    setSettlementProductSearchTerm('');
    setSettlementProductSearchResults([]);
    setShowCloseTradeModal(true);
  };

  // Search products for settlement
  const searchSettlementProducts = async (searchTerm: string, loadMore: boolean = false) => {
    if (!companyId) return;
    
    if (searchTerm.length < 2) {
      setSettlementProductSearchResults([]);
      setSettlementProductSearchHasMore(false);
      return;
    }

    setSettlementProductSearchLoading(true);
    try {
      const pageSize = 20;
      const currentResults = loadMore ? settlementProductSearchResults : [];
      const currentCount = currentResults.length;
      
      // Get more results (searchProducts handles pagination internally, but we need to accumulate)
      const results = await productService.searchProducts(companyId, searchTerm, currentCount + pageSize);
      const newResults = results.slice(currentCount);
      
      setSettlementProductSearchResults([...currentResults, ...newResults]);
      setSettlementProductSearchHasMore(newResults.length === pageSize && results.length >= currentCount + pageSize);
    } catch (error) {
      console.error('Error searching settlement products:', error);
      if (!loadMore) {
        setSettlementProductSearchResults([]);
      }
    } finally {
      setSettlementProductSearchLoading(false);
    }
  };

  // Debounced search for settlement products
  useEffect(() => {
    const timer = setTimeout(() => {
      if (settlementProductSearchTerm.length >= 2) {
        searchSettlementProducts(settlementProductSearchTerm, false);
      } else {
        setSettlementProductSearchResults([]);
        setSettlementProductSearchHasMore(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [settlementProductSearchTerm, companyId]);

  const handleCloseTrade = async () => {
    if (!companyId || !user || !tradeToClose) return;

    // Validate that at least one item is selected
    if (closeTradeForm.selectedItemIds.length === 0) {
      toast({
        title: 'Hata',
        description: 'En az bir ürün seçmelisiniz',
        variant: 'destructive'
      });
      return;
    }

    // Validate settlement - require product for same_product and replacement_product
    if (closeTradeForm.settlement_type === 'replacement_product' || closeTradeForm.settlement_type === 'same_product') {
      if (!closeTradeForm.settlement_product_id || closeTradeForm.settlement_product_id.trim() === '') {
        toast({
          title: 'Hata',
          description: 'Kapanış ürünü seçimi zorunludur',
          variant: 'destructive'
        });
        return;
      }
      if (closeTradeForm.settlement_quantity <= 0) {
        toast({
          title: 'Hata',
          description: 'Kapanış ürünü adedi 0\'dan büyük olmalıdır',
          variant: 'destructive'
        });
        return;
      }
    }

    try {
      // Prepare settlement values - ensure empty strings are converted to null
      const settlementProductId = closeTradeForm.settlement_type !== 'cash' && closeTradeForm.settlement_product_id && closeTradeForm.settlement_product_id.trim() !== ''
        ? closeTradeForm.settlement_product_id
        : null;
      const settlementQuantity = closeTradeForm.settlement_type !== 'cash' && settlementProductId
        ? closeTradeForm.settlement_quantity
        : 0;

      // Mark selected items as completed
      const { error: itemsUpdateError } = await supabase
        .from('trade_items')
        .update({ status: 'completed' })
        .in('id', closeTradeForm.selectedItemIds);

      if (itemsUpdateError) throw itemsUpdateError;

      // Query database to check if all items are now completed (don't rely on stale local state)
      const { data: allTradeItems, error: itemsFetchError } = await supabase
        .from('trade_items')
        .select('id, status')
        .eq('trade_id', tradeToClose.id)
        .eq('company_id', companyId);

      if (itemsFetchError) throw itemsFetchError;

      // Check if all items are completed
      const allItems = allTradeItems || [];
      const allCompleted = allItems.length > 0 && allItems.every((item: any) => item.status === 'completed');

      // Always update settlement info, and update status if all items are completed
      const updateData: any = {
        settlement_type: closeTradeForm.settlement_type,
        settlement_amount: closeTradeForm.settlement_type === 'cash' ? closeTradeForm.settlement_amount : 0,
        settlement_product_id: settlementProductId,
        settlement_quantity: settlementQuantity,
        updated_at: new Date().toISOString()
      };

      // Only update status to completed if all items are completed
      if (allCompleted) {
        updateData.status = 'completed';
      }

      const { error: updateError } = await supabase
        .from('trades')
        .update(updateData)
        .eq('id', tradeToClose.id);

      if (updateError) {
        console.error('Error updating trade:', updateError);
        console.error('Update data:', updateData);
        throw updateError;
      }

      // Handle inventory adjustments for selected items only
      await handleInventoryAdjustmentsForClose(tradeToClose.id, closeTradeForm, tradeToClose);

      const itemCount = closeTradeForm.selectedItemIds.length;
      const totalItems = allItems.length;
      const message = allCompleted
        ? 'Ticaret kaydı başarıyla kapatıldı'
        : `${itemCount} ürün kapatıldı, ${totalItems - itemCount} ürün bekliyor`;

      toast({
        title: 'Başarılı',
        description: message
      });

      setShowCloseTradeModal(false);
      setTradeToClose(null);
      fetchTrades();
    } catch (error: any) {
      console.error('Error closing trade:', error);
      toast({
        title: 'Hata',
        description: error.message || 'Ticaret kaydı kapatılırken bir hata oluştu',
        variant: 'destructive'
      });
    }
  };

  const handleInventoryAdjustmentsOnCreate = async (
    tradeId: string,
    trade: typeof newTrade
  ) => {
    if (!companyId) return;

    try {
      // Adjust inventory based on trade type when creating
      // Biz Veriyoruz (we_give): deduct from inventory
      // Biz Alıyoruz (we_receive): add to inventory
      for (const item of trade.items) {
        const product = products.find(p => p.id === item.product_id);
        if (!product) continue;

        // Determine adjustment based on trade type
        // Biz Veriyoruz: deduct (negative adjustment)
        // Biz Alıyoruz: add (positive adjustment)
        const adjustment = trade.trade_type === 'we_give' 
          ? -item.quantity  // Deduct when we give
          : item.quantity;  // Add when we receive

        const newQuantity = product.quantity_on_hand + adjustment;

        // Update product inventory
        const { error: updateError } = await supabase
          .from('products')
          .update({ quantity_on_hand: newQuantity })
          .eq('id', item.product_id);

        if (updateError) throw updateError;

        // Create inventory movement
        await supabase
          .from('inventory_movements')
          .insert({
            company_id: companyId,
            product_id: item.product_id,
            movement_type: adjustment > 0 ? 'purchase_in' : 'adjustment_out',
            qty: Math.abs(adjustment),
            related_table: 'trades',
            related_id: tradeId,
            actor_user_id: user?.id || null
          });
      }
    } catch (error) {
      console.error('Error adjusting inventory on create:', error);
      throw error;
    }
  };

  const handleInventoryAdjustmentsForClose = async (
    tradeId: string, 
    settlement: typeof closeTradeForm,
    trade: Trade
  ) => {
    if (!companyId) return;

    try {
      // Reverse the initial inventory adjustments only for selected items
      const tradeItems = trade.trade_items || [];
      const selectedItems = tradeItems.filter((item: TradeItem) => 
        settlement.selectedItemIds.includes(item.id) && item.status !== 'completed'
      );
      
      if (selectedItems.length > 0) {
        for (const item of selectedItems) {
          const product = products.find(p => p.id === item.product_id);
          if (!product) continue;

          // Reverse the adjustment: opposite of what was done on create
          // If we gave (deducted), now reverse by adding
          // If we received (added), now reverse by deducting
          const adjustment = trade.trade_type === 'we_give' 
            ? item.quantity   // Reverse: we deducted, now add back
            : -item.quantity; // Reverse: we added, now deduct

          const newQuantity = product.quantity_on_hand + adjustment;

          // Update product inventory
          const { error: updateError } = await supabase
            .from('products')
            .update({ quantity_on_hand: newQuantity })
            .eq('id', item.product_id);

          if (updateError) throw updateError;

          // Create inventory movement for reversal
          await supabase
            .from('inventory_movements')
            .insert({
              company_id: companyId,
              product_id: item.product_id,
              movement_type: adjustment > 0 ? 'purchase_in' : 'adjustment_out',
              qty: Math.abs(adjustment),
              related_table: 'trades',
              related_id: tradeId,
              actor_user_id: user?.id || null
            });
        }
      }

      // Handle settlement inventory adjustments
      if (settlement.settlement_type === 'same_product' || settlement.settlement_type === 'replacement_product') {
        if (settlement.settlement_product_id) {
          const settlementProduct = products.find(p => p.id === settlement.settlement_product_id);
          if (settlementProduct) {
            // Settlement product is what we're receiving to close the trade
            const settlementAdjustment = trade.trade_type === 'we_receive' 
              ? -settlement.settlement_quantity 
              : settlement.settlement_quantity;
            
            const newSettlementQuantity = settlementProduct.quantity_on_hand + settlementAdjustment;

            // Update settlement product inventory
            const { error: settlementUpdateError } = await supabase
              .from('products')
              .update({ quantity_on_hand: newSettlementQuantity })
              .eq('id', settlement.settlement_product_id);

            if (settlementUpdateError) throw settlementUpdateError;

            // Create inventory movement for settlement
            await supabase
              .from('inventory_movements')
              .insert({
                company_id: companyId,
                product_id: settlement.settlement_product_id,
                movement_type: settlementAdjustment > 0 ? 'purchase_in' : 'adjustment_out',
                qty: Math.abs(settlementAdjustment),
                related_table: 'trades',
                related_id: tradeId,
                actor_user_id: user?.id || null
              });
          }
        }
      }
    } catch (error) {
      console.error('Error adjusting inventory:', error);
      throw error;
    }
  };

  // Calculate totals by Bayi
  const totalsByBayi = useMemo(() => {
    const totals: Record<string, {
      bayi: Bayi;
      openTrades: number;
      closedTrades: number;
      totalValue: number;
      totalCost: number;
    }> = {};

    trades.forEach(trade => {
      // Handle nested data structure
      const bayi = trade.bayi || (trade as any).bayis;
      if (!bayi) return;
      
      const bayiId = trade.bayi_id;
      if (!totals[bayiId]) {
        totals[bayiId] = {
          bayi: bayi,
          openTrades: 0,
          closedTrades: 0,
          totalValue: 0,
          totalCost: 0
        };
      }

      if (trade.status === 'pending') {
        totals[bayiId].openTrades++;
      } else if (trade.status === 'completed') {
        totals[bayiId].closedTrades++;
      }

      // Calculate total value and cost from trade items
      const tradeItems = trade.trade_items || [];
      tradeItems.forEach((item: any) => {
        const product = item.product || item.products;
        const itemValue = (item.unit_price || 0) * item.quantity;
        const itemCost = (product?.current_cost || 0) * item.quantity;
        
        if (item.direction === 'out') {
          totals[bayiId].totalValue += itemValue;
          totals[bayiId].totalCost += itemCost;
        } else {
          totals[bayiId].totalValue -= itemValue;
          totals[bayiId].totalCost -= itemCost;
        }
      });
    });

    return Object.values(totals);
  }, [trades]);

  // Filter trades
  const filteredTrades = useMemo(() => {
    return trades.filter(trade => {
      const matchesSearch = searchTerm === '' || 
        trade.bayi?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        trade.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        trade.trade_items?.some(item => 
          item.product?.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
      
      const matchesBayi = selectedBayi === 'all' || trade.bayi_id === selectedBayi;
      const matchesStatus = statusFilter === 'all' || trade.status === statusFilter;
      
      return matchesSearch && matchesBayi && matchesStatus;
    });
  }, [trades, searchTerm, selectedBayi, statusFilter]);

  // Separate and group trades: open trades individually, closed trades grouped by bayi
  const { openTrades, closedTradesByBayi } = useMemo(() => {
    const open: Trade[] = [];
    const closedByBayi: Record<string, { bayi: Bayi; trades: Trade[] }> = {};

    filteredTrades.forEach(trade => {
      const bayi = trade.bayi || (trade as any).bayis;
      
      if (trade.status === 'completed' || trade.status === 'cancelled') {
        // Group closed trades by bayi
        const bayiId = trade.bayi_id;
        const bayiName = bayi?.name || 'Bilinmeyen Bayi';
        const key = `${bayiId}-${bayiName}`;
        
        if (!closedByBayi[key]) {
          closedByBayi[key] = {
            bayi: bayi || { 
              id: bayiId, 
              name: bayiName, 
              color: '#8B5CF6',
              contact_person: null,
              phone: null,
              email: null,
              address: null,
              notes: null,
              is_active: true,
              created_at: ''
            },
            trades: []
          };
        }
        closedByBayi[key].trades.push(trade);
      } else {
        // Keep open trades individually
        open.push(trade);
      }
    });

    // Sort closed trades within each group by date (newest first)
    Object.values(closedByBayi).forEach(group => {
      group.trades.sort((a, b) => 
        new Date(b.trade_date).getTime() - new Date(a.trade_date).getTime()
      );
    });

    return { 
      openTrades: open, 
      closedTradesByBayi: Object.values(closedByBayi).sort((a, b) => 
        a.bayi.name.localeCompare(b.bayi.name, 'tr')
      )
    };
  }, [filteredTrades]);

  // Helper function to render a single trade card
  const renderTradeCard = (trade: Trade, isGrouped: boolean = false) => {
    const TradeTypeIcon = tradeTypeLabels[trade.trade_type]?.icon || ArrowRightLeft;
    const SettlementIcon = settlementTypeLabels[trade.settlement_type]?.icon || DollarSign;
    
    // Handle nested data structure from Supabase
    const bayi = trade.bayi || (trade as any).bayis;
    const tradeItems = trade.trade_items || [];
    
    return (
      <Card key={trade.id} className={cn("hover:shadow-md transition-shadow", isGrouped && "border-l-4")}>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4 flex-1">
              {!isGrouped && bayi && (
                <div 
                  className="w-4 h-4 rounded-full mt-1 flex-shrink-0" 
                  style={{ backgroundColor: bayi.color || '#8B5CF6' }}
                />
              )}
              <div className="flex-1">
                {!isGrouped && (
                  <div className="flex items-center gap-2 mb-2">
                    <CardTitle className="text-lg">
                      {bayi?.name || 'Bilinmeyen Bayi'}
                    </CardTitle>
                    <Badge className={tradeTypeLabels[trade.trade_type].color}>
                      <TradeTypeIcon className="w-3 h-3 mr-1" />
                      {tradeTypeLabels[trade.trade_type].labelPast || tradeTypeLabels[trade.trade_type].label}
                    </Badge>
                    <Badge className={statusLabels[trade.status].color}>
                      {statusLabels[trade.status].label}
                    </Badge>
                  </div>
                )}
                {isGrouped && (
                  <div className="flex items-center gap-2 mb-2">
                    <CardTitle className="text-base">
                      {format(new Date(trade.trade_date), 'PPP', { locale: tr })}
                    </CardTitle>
                    <Badge className={tradeTypeLabels[trade.trade_type].color}>
                      <TradeTypeIcon className="w-3 h-3 mr-1" />
                      {tradeTypeLabels[trade.trade_type].labelPast || tradeTypeLabels[trade.trade_type].label}
                    </Badge>
                    <Badge className={statusLabels[trade.status].color}>
                      {statusLabels[trade.status].label}
                    </Badge>
                  </div>
                )}
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  {isGrouped && (
                    <span>{bayi?.name || 'Bilinmeyen Bayi'}</span>
                  )}
                  {!isGrouped && (
                    <span>{format(new Date(trade.trade_date), 'PPP', { locale: tr })}</span>
                  )}
                  {trade.status === 'completed' && (
                    <>
                      <span className="flex items-center gap-1">
                        <SettlementIcon className="w-4 h-4" />
                        {settlementTypeLabels[trade.settlement_type].label}
                      </span>
                      {trade.settlement_type === 'cash' && trade.settlement_amount > 0 && (
                        <span className="font-semibold text-foreground">
                          {formatPrice(trade.settlement_amount)}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            {trade.status === 'pending' && !isGrouped && (
              <Button
                variant="default"
                size="sm"
                onClick={() => handleOpenCloseTradeModal(trade)}
                className="gap-2 bg-green-600 hover:bg-green-700 text-white"
              >
                <X className="w-4 h-4" />
                Ticareti Kapat
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {tradeItems && tradeItems.length > 0 && (
            <div className="space-y-2 mb-4">
              <Label className="text-sm font-semibold">Ürünler:</Label>
              {tradeItems.map((item: any) => {
                // Handle nested product data structure
                const product = item.product || item.products;
                const isCompleted = item.status === 'completed';
                return (
                  <div 
                    key={item.id} 
                    className={cn(
                      "flex items-center justify-between p-2 rounded border",
                      isCompleted ? "bg-green-500/10 border-green-500/20" : "bg-muted/50"
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Badge variant={item.direction === 'out' ? 'destructive' : 'default'}>
                        {item.direction === 'out' ? 'Çıkış' : 'Giriş'}
                      </Badge>
                      <span className={cn("font-medium", isCompleted && "line-through opacity-60")}>
                        {product?.name || 'Bilinmeyen Ürün'}
                      </span>
                      {product?.item_code && (
                        <span className="text-sm text-muted-foreground">
                          ({product.item_code})
                        </span>
                      )}
                      {isCompleted && (
                        <Badge variant="outline" className="text-xs bg-green-500/20 text-green-700 dark:text-green-400">
                          Kapatıldı
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={cn("text-sm", isCompleted && "line-through opacity-60")}>
                        {item.quantity} adet
                      </span>
                      {item.unit_price && (
                        <span className={cn("text-sm font-semibold", isCompleted && "line-through opacity-60")}>
                          {formatPrice(item.unit_price)} × {item.quantity} = {formatPrice(item.total_price || 0)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {(trade.settlement_type === 'same_product' || trade.settlement_type === 'replacement_product') && trade.settlement_product_id && (
            <div className="p-2 bg-blue-500/10 rounded mb-4">
              <div className="text-sm">
                <span className="font-semibold">Kapanış:</span>{' '}
                {products.find(p => p.id === trade.settlement_product_id)?.name || 'Bilinmeyen Ürün'} × {trade.settlement_quantity}
              </div>
            </div>
          )}
          
          {trade.notes && (
            <div className="text-sm text-muted-foreground">
              <span className="font-semibold">Not:</span> {trade.notes}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading && trades.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Bayi Ticareti</h1>
            <p className="text-muted-foreground mt-1">
              Diğer satıcılarla (Bayi) olan ticaret kayıtlarınızı yönetin
            </p>
          </div>
          <div className="flex gap-2">
            <Dialog open={showNewBayiModal} onOpenChange={setShowNewBayiModal}>
              <DialogTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <UserPlus className="w-4 h-4" />
                  Yeni Bayi
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Yeni Bayi Ekle</DialogTitle>
                  <DialogDescription>
                    Ticaret yaptığınız yeni bir bayi ekleyin
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Bayi Adı *</Label>
                    <Input
                      value={newBayi.name}
                      onChange={(e) => setNewBayi(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Bayi adı..."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>İletişim Kişisi</Label>
                      <Input
                        value={newBayi.contact_person}
                        onChange={(e) => setNewBayi(prev => ({ ...prev, contact_person: e.target.value }))}
                        placeholder="İletişim kişisi..."
                      />
                    </div>
                    <div>
                      <Label>Telefon</Label>
                      <Input
                        value={newBayi.phone}
                        onChange={(e) => setNewBayi(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="Telefon..."
                      />
                    </div>
                  </div>
                  <div>
                    <Label>E-posta</Label>
                    <Input
                      type="email"
                      value={newBayi.email}
                      onChange={(e) => setNewBayi(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="E-posta..."
                    />
                  </div>
                  <div>
                    <Label>Adres</Label>
                    <Textarea
                      value={newBayi.address}
                      onChange={(e) => setNewBayi(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="Adres..."
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Notlar</Label>
                    <Textarea
                      value={newBayi.notes}
                      onChange={(e) => setNewBayi(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Notlar..."
                      rows={3}
                    />
                  </div>
                  <Button onClick={handleCreateBayi} className="w-full">
                    Bayi Ekle
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={showNewTradeModal} onOpenChange={setShowNewTradeModal}>
              <DialogTrigger asChild>
                <Button className="gap-2">
                  <Plus className="w-4 h-4" />
                  Yeni Ticaret
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl">
                <DialogHeader>
                  <DialogTitle>Yeni Ticaret Kaydı</DialogTitle>
                  <DialogDescription>
                    Bayi ile yeni bir ticaret kaydı oluşturun
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Bayi *</Label>
                      <Select value={newTrade.bayi_id} onValueChange={(value) => setNewTrade(prev => ({ ...prev, bayi_id: value }))}>
                        <SelectTrigger>
                          <SelectValue placeholder="Bayi seçin..." />
                        </SelectTrigger>
                        <SelectContent>
                          {bayis.map(bayi => (
                            <SelectItem key={bayi.id} value={bayi.id}>
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: bayi.color }} />
                                {bayi.name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Tarih *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !newTrade.trade_date && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {newTrade.trade_date ? format(newTrade.trade_date, 'PPP', { locale: tr }) : "Tarih seçin"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={newTrade.trade_date}
                            onSelect={(date) => date && setNewTrade(prev => ({ ...prev, trade_date: date }))}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  <div>
                    <Label>Ticaret Tipi *</Label>
                    <Select value={newTrade.trade_type} onValueChange={(value: any) => setNewTrade(prev => ({ ...prev, trade_type: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="we_give">Biz Veriyoruz</SelectItem>
                        <SelectItem value="we_receive">Biz Alıyoruz</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Kapanış bilgileri ticareti kapatırken belirlenecektir
                    </p>
                  </div>

                  <div>
                    <Label>Ürünler</Label>
                    <div className="space-y-2">
                      {newTrade.items.map((item, index) => (
                        <Card key={index} className="p-4">
                          <div className="grid grid-cols-12 gap-2 items-end">
                            <div className="col-span-5">
                              <Label className="text-xs">Ürün</Label>
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                  placeholder="Ürün ara veya seç..."
                                  value={productSearchTerms[index] || ''}
                                  onChange={(e) => {
                                    setProductSearchTerms(prev => ({ ...prev, [index]: e.target.value }));
                                    if (!e.target.value) {
                                      setProductSearchResults(prev => ({ ...prev, [index]: [] }));
                                    }
                                  }}
                                  onFocus={() => {
                                    if (productSearchTerms[index] && productSearchTerms[index].length >= 2) {
                                      searchProductsForItem(index, productSearchTerms[index]);
                                    }
                                  }}
                                  className="pl-10"
                                />
                                {productSearchLoading[index] && (
                                  <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                                )}
                                {productSearchResults[index] && productSearchResults[index].length > 0 && (
                                  <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-60 overflow-y-auto">
                                    {productSearchResults[index].map(product => (
                                      <div
                                        key={product.id}
                                        onClick={() => {
                                          updateTradeItem(index, 'product_id', product.id);
                                          setProductSearchTerms(prev => ({ ...prev, [index]: product.name }));
                                          setProductSearchResults(prev => ({ ...prev, [index]: [] }));
                                        }}
                                        className="px-4 py-2 hover:bg-accent cursor-pointer flex items-center justify-between"
                                      >
                                        <div>
                                          <div className="font-medium">{product.name}</div>
                                          <div className="text-xs text-muted-foreground">{product.item_code}</div>
                                        </div>
                                        {product.current_cost && (
                                          <div className="text-xs text-muted-foreground">
                                            {formatPrice(product.current_cost)}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              {item.product_id && (
                                <div className="mt-1 text-xs text-muted-foreground">
                                  Seçili: {products.find(p => p.id === item.product_id)?.name || 
                                           Object.values(productSearchResults).flat().find(p => p.id === item.product_id)?.name || 
                                           'Bilinmeyen'}
                                </div>
                              )}
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs">Yön</Label>
                              <Select 
                                value={item.direction} 
                                onValueChange={(value: any) => updateTradeItem(index, 'direction', value)}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="out">Çıkış</SelectItem>
                                  <SelectItem value="in">Giriş</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs">Adet</Label>
                              <Input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => updateTradeItem(index, 'quantity', parseInt(e.target.value) || 1)}
                              />
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs">Birim Fiyat (Maliyet)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={item.unit_price}
                                onChange={(e) => updateTradeItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                                placeholder="0.00"
                              />
                              {item.product_id && products.find(p => p.id === item.product_id)?.current_cost && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Güncel maliyet: {formatPrice(products.find(p => p.id === item.product_id)!.current_cost || 0)}
                                </p>
                              )}
                            </div>
                            <div className="col-span-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => removeTradeItem(index)}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </Card>
                      ))}
                      <Button type="button" variant="outline" onClick={addTradeItem} className="w-full">
                        <Plus className="w-4 h-4 mr-2" />
                        Ürün Ekle
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label>Notlar</Label>
                    <Textarea
                      value={newTrade.notes}
                      onChange={(e) => setNewTrade(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Notlar..."
                      rows={3}
                    />
                  </div>

                  <Button onClick={handleCreateTrade} className="w-full">
                    Ticaret Kaydı Oluştur
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Ara</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Bayi, ürün veya not ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <Label>Bayi</Label>
                <Select value={selectedBayi} onValueChange={setSelectedBayi}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    {bayis.map(bayi => (
                      <SelectItem key={bayi.id} value={bayi.id}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: bayi.color }} />
                          {bayi.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Durum</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    <SelectItem value="pending">Beklemede</SelectItem>
                    <SelectItem value="completed">Tamamlandı</SelectItem>
                    <SelectItem value="cancelled">İptal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Totals by Bayi */}
        {totalsByBayi.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Bayi Bazında Özet</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {totalsByBayi.map(total => (
                  <Card key={total.bayi.id} className="p-4">
                    <div className="flex items-start gap-3 mb-3">
                      <div 
                        className="w-4 h-4 rounded-full mt-1 flex-shrink-0" 
                        style={{ backgroundColor: total.bayi.color }}
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold">{total.bayi.name}</h3>
                        <div className="flex gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            {total.openTrades} Açık
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {total.closedTrades} Kapatıldı
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Toplam Değer:</span>
                        <span className="font-semibold">{formatPrice(total.totalValue)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Toplam Maliyet:</span>
                        <span className="font-semibold">{formatPrice(total.totalCost)}</span>
                      </div>
                      {total.totalValue !== 0 && (
                        <div className="flex justify-between pt-1 border-t">
                          <span className="text-muted-foreground">Net:</span>
                          <span className={cn(
                            "font-bold",
                            total.totalValue - total.totalCost >= 0 ? "text-green-600" : "text-red-600"
                          )}>
                            {formatPrice(total.totalValue - total.totalCost)}
                          </span>
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Trades List */}
        <div className="space-y-4">
          {openTrades.length === 0 && closedTradesByBayi.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <ShoppingBag className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Henüz ticaret kaydı bulunmamaktadır</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Open Trades - Display individually */}
              {openTrades.length > 0 && (
                <div className="space-y-4">
                  <div className="mb-2">
                    <h2 className="text-xl font-semibold">Açık Ticaretler</h2>
                    <p className="text-sm text-muted-foreground">{openTrades.length} açık ticaret kaydı</p>
                  </div>
                  {openTrades.map(trade => renderTradeCard(trade))}
                </div>
              )}

              {/* Closed Trades - Grouped by Bayi */}
              {closedTradesByBayi.length > 0 && (
                <div className="space-y-4">
                  {openTrades.length > 0 && <div className="border-t pt-6 mt-6" />}
                  <div className="mb-2">
                    <h2 className="text-xl font-semibold">Kapatılmış Ticaretler</h2>
                    <p className="text-sm text-muted-foreground">
                      {closedTradesByBayi.reduce((sum, group) => sum + group.trades.length, 0)} kapatılmış ticaret kaydı, {closedTradesByBayi.length} bayi
                    </p>
                  </div>
                  {closedTradesByBayi.map((group, groupIndex) => {
                    const groupKey = `${group.bayi.id}-${groupIndex}`;
                    const isExpanded = expandedGroups.has(groupKey);
                    
                    return (
                      <Collapsible
                        key={groupKey}
                        open={isExpanded}
                        onOpenChange={(open) => {
                          setExpandedGroups(prev => {
                            const next = new Set(prev);
                            if (open) {
                              next.add(groupKey);
                            } else {
                              next.delete(groupKey);
                            }
                            return next;
                          });
                        }}
                      >
                        <Card className="hover:shadow-md transition-shadow">
                          <CollapsibleTrigger asChild>
                            <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3 flex-1">
                                  <div 
                                    className="w-5 h-5 rounded-full flex-shrink-0" 
                                    style={{ backgroundColor: group.bayi.color || '#8B5CF6' }}
                                  />
                                  <div className="flex-1">
                                    <CardTitle className="text-lg">
                                      {group.bayi.name}
                                    </CardTitle>
                                    <div className="flex items-center gap-2 mt-1">
                                      <Badge variant="outline" className="text-xs">
                                        {group.trades.length} Ticaret
                                      </Badge>
                                      <span className="text-sm text-muted-foreground">
                                        {group.trades.filter(t => t.status === 'completed').length} Tamamlandı
                                        {group.trades.filter(t => t.status === 'cancelled').length > 0 && 
                                          ` • ${group.trades.filter(t => t.status === 'cancelled').length} İptal`
                                        }
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <ChevronDown className={cn(
                                  "w-5 h-5 text-muted-foreground transition-transform duration-200",
                                  isExpanded && "rotate-180"
                                )} />
                              </div>
                            </CardHeader>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <CardContent className="pt-0">
                              <div className="space-y-3">
                                {group.trades.map(trade => renderTradeCard(trade, true))}
                              </div>
                            </CardContent>
                          </CollapsibleContent>
                        </Card>
                      </Collapsible>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Close Trade Modal */}
        <Dialog open={showCloseTradeModal} onOpenChange={setShowCloseTradeModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Ticareti Kapat</DialogTitle>
              <DialogDescription>
                {tradeToClose && (() => {
                  const bayi = tradeToClose.bayi || (tradeToClose as any).bayis;
                  const bayiName = bayi?.name || 'Bilinmeyen Bayi';
                  return `"${bayiName}" ile olan ticareti kapatmak için kapanış bilgilerini girin`;
                })()}
              </DialogDescription>
            </DialogHeader>
            {tradeToClose && (
              <div className="space-y-4">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <div className="text-sm font-semibold mb-2">Ticaret Özeti:</div>
                  <div className="text-sm space-y-1">
                    <div>Tarih: {format(new Date(tradeToClose.trade_date), 'PPP', { locale: tr })}</div>
                    <div>Tip: {tradeTypeLabels[tradeToClose.trade_type].label}</div>
                  </div>
                </div>

                {/* Item Selection */}
                {tradeToClose.trade_items && tradeToClose.trade_items.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <Label>Kapatılacak Ürünleri Seçin *</Label>
                      {(() => {
                        const pendingItems = tradeToClose.trade_items.filter((item: TradeItem) => item.status !== 'completed');
                        const allSelected = pendingItems.length > 0 && pendingItems.every((item: TradeItem) => 
                          closeTradeForm.selectedItemIds.includes(item.id)
                        );
                        return pendingItems.length > 0 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => {
                              if (allSelected) {
                                setCloseTradeForm(prev => ({
                                  ...prev,
                                  selectedItemIds: []
                                }));
                              } else {
                                setCloseTradeForm(prev => ({
                                  ...prev,
                                  selectedItemIds: pendingItems.map((item: TradeItem) => item.id)
                                }));
                              }
                            }}
                          >
                            {allSelected ? 'Tümünü Kaldır' : 'Tümünü Seç'}
                          </Button>
                        );
                      })()}
                    </div>
                    <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-3">
                      {tradeToClose.trade_items.map((item: TradeItem) => {
                        const product = item.product || (item as any).products;
                        const productName = product?.name || 'Bilinmeyen Ürün';
                        const isCompleted = item.status === 'completed';
                        const isSelected = closeTradeForm.selectedItemIds.includes(item.id);
                        
                        return (
                          <div
                            key={item.id}
                            className={cn(
                              "flex items-start gap-3 p-3 rounded-lg border transition-colors",
                              isCompleted && "opacity-50 bg-muted/30",
                              !isCompleted && isSelected && "bg-primary/10 border-primary",
                              !isCompleted && !isSelected && "hover:bg-muted/50 cursor-pointer"
                            )}
                            onClick={() => {
                              if (!isCompleted) {
                                setCloseTradeForm(prev => ({
                                  ...prev,
                                  selectedItemIds: isSelected
                                    ? prev.selectedItemIds.filter(id => id !== item.id)
                                    : [...prev.selectedItemIds, item.id]
                                }));
                              }
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={isCompleted}
                              onChange={() => {
                                if (!isCompleted) {
                                  setCloseTradeForm(prev => ({
                                    ...prev,
                                    selectedItemIds: isSelected
                                      ? prev.selectedItemIds.filter(id => id !== item.id)
                                      : [...prev.selectedItemIds, item.id]
                                  }));
                                }
                              }}
                              className="mt-1"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{productName}</span>
                                {isCompleted && (
                                  <Badge variant="outline" className="text-xs">
                                    Kapatıldı
                                  </Badge>
                                )}
                              </div>
                              <div className="text-sm text-muted-foreground mt-1">
                                {item.direction === 'out' ? 'Çıkış' : 'Giriş'}: {item.quantity} adet
                                {item.unit_price && (
                                  <span className="ml-2">
                                    • {formatPrice(item.unit_price)} × {item.quantity} = {formatPrice(item.total_price || 0)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="text-xs text-muted-foreground mt-2">
                      {closeTradeForm.selectedItemIds.length} ürün seçildi
                    </div>
                  </div>
                )}

                <div>
                  <Label>Kapanış Şekli *</Label>
                  <Select 
                    value={closeTradeForm.settlement_type} 
                    onValueChange={(value: any) => {
                      // Reset settlement fields when changing settlement type
                      const newForm = {
                        ...closeTradeForm,
                        settlement_type: value,
                        settlement_amount: 0,
                        settlement_product_id: '',
                        settlement_quantity: 1
                      };
                      
                      // Auto-select product for same_product if there's only one selected item
                      if (value === 'same_product' && tradeToClose && closeTradeForm.selectedItemIds.length === 1) {
                        const selectedItemId = closeTradeForm.selectedItemIds[0];
                        const selectedItem = tradeToClose.trade_items?.find((item: TradeItem) => item.id === selectedItemId);
                        if (selectedItem) {
                          newForm.settlement_product_id = selectedItem.product_id;
                          newForm.settlement_quantity = selectedItem.quantity;
                        }
                      }
                      
                      setCloseTradeForm(newForm);
                      if (value !== 'replacement_product') {
                        setSettlementProductSearchTerm('');
                        setSettlementProductSearchResults([]);
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Nakit</SelectItem>
                      <SelectItem value="same_product">Aynı Ürün</SelectItem>
                      <SelectItem value="replacement_product">Değişim Ürünü</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {closeTradeForm.settlement_type === 'cash' && (
                  <div>
                    <Label>Nakit Tutarı (₺) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={closeTradeForm.settlement_amount}
                      onChange={(e) => setCloseTradeForm(prev => ({ ...prev, settlement_amount: parseFloat(e.target.value) || 0 }))}
                      placeholder="0.00"
                    />
                  </div>
                )}

                {closeTradeForm.settlement_type === 'same_product' && (
                  <div className="space-y-4">
                    <div>
                      <Label>Kapanış Ürünü (Ticaret Ürünlerinden Seçin) *</Label>
                      <Select
                        value={closeTradeForm.settlement_product_id}
                        onValueChange={(value) => {
                          setCloseTradeForm(prev => ({ ...prev, settlement_product_id: value }));
                          // Auto-set quantity from selected trade item if available
                          if (tradeToClose) {
                            const selectedItem = tradeToClose.trade_items?.find(
                              (item: TradeItem) => item.product_id === value && closeTradeForm.selectedItemIds.includes(item.id)
                            );
                            if (selectedItem) {
                              setCloseTradeForm(prev => ({ ...prev, settlement_quantity: selectedItem.quantity }));
                            }
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Ürün seçin..." />
                        </SelectTrigger>
                        <SelectContent>
                          {tradeToClose && tradeToClose.trade_items
                            ?.filter((item: TradeItem) => closeTradeForm.selectedItemIds.includes(item.id))
                            .map((item: TradeItem) => {
                              const product = item.product || (item as any).products;
                              return (
                                <SelectItem key={item.product_id} value={item.product_id}>
                                  {product?.name || 'Bilinmeyen Ürün'} ({item.quantity} adet)
                                </SelectItem>
                              );
                            })}
                        </SelectContent>
                      </Select>
                      {closeTradeForm.settlement_product_id && (
                        <div className="mt-2 text-sm text-muted-foreground">
                          Seçili: {(() => {
                            const selectedItem = tradeToClose?.trade_items?.find(
                              (item: TradeItem) => item.product_id === closeTradeForm.settlement_product_id
                            );
                            const product = selectedItem?.product || (selectedItem as any)?.products;
                            return product?.name || products.find(p => p.id === closeTradeForm.settlement_product_id)?.name || 'Bilinmeyen';
                          })()}
                        </div>
                      )}
                    </div>
                    <div>
                      <Label>Adet *</Label>
                      <Input
                        type="number"
                        min="1"
                        value={closeTradeForm.settlement_quantity}
                        onChange={(e) => setCloseTradeForm(prev => ({ ...prev, settlement_quantity: parseInt(e.target.value) || 1 }))}
                      />
                    </div>
                  </div>
                )}

                {closeTradeForm.settlement_type === 'replacement_product' && (
                  <div className="space-y-4">
                    <div>
                      <Label>Kapanış Ürünü *</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="Ürün ara..."
                          value={settlementProductSearchTerm}
                          onChange={(e) => setSettlementProductSearchTerm(e.target.value)}
                          className="pl-10"
                        />
                        {settlementProductSearchLoading && (
                          <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                        )}
                        {settlementProductSearchResults.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {settlementProductSearchResults.map(product => (
                              <div
                                key={product.id}
                                onClick={() => {
                                  setCloseTradeForm(prev => ({ ...prev, settlement_product_id: product.id }));
                                  setSettlementProductSearchTerm(product.name);
                                  setSettlementProductSearchResults([]);
                                }}
                                className={cn(
                                  "px-4 py-2 hover:bg-accent cursor-pointer flex items-center justify-between",
                                  closeTradeForm.settlement_product_id === product.id && "bg-accent"
                                )}
                              >
                                <div>
                                  <div className="font-medium">{product.name}</div>
                                  <div className="text-xs text-muted-foreground">{product.item_code}</div>
                                </div>
                                {product.current_cost && (
                                  <div className="text-xs text-muted-foreground">
                                    {formatPrice(product.current_cost)}
                                  </div>
                                )}
                              </div>
                            ))}
                            {settlementProductSearchHasMore && (
                              <div
                                onClick={() => searchSettlementProducts(settlementProductSearchTerm, true)}
                                className="px-4 py-2 hover:bg-accent cursor-pointer text-center text-sm text-muted-foreground border-t"
                              >
                                Daha fazla yükle...
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {closeTradeForm.settlement_product_id && (
                        <div className="mt-2 text-sm text-muted-foreground">
                          Seçili: {settlementProductSearchResults.find(p => p.id === closeTradeForm.settlement_product_id)?.name || 
                                   products.find(p => p.id === closeTradeForm.settlement_product_id)?.name || 
                                   'Bilinmeyen'}
                        </div>
                      )}
                    </div>
                    <div>
                      <Label>Adet *</Label>
                      <Input
                        type="number"
                        min="1"
                        value={closeTradeForm.settlement_quantity}
                        onChange={(e) => setCloseTradeForm(prev => ({ ...prev, settlement_quantity: parseInt(e.target.value) || 1 }))}
                      />
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowCloseTradeModal(false);
                      setTradeToClose(null);
                    }}
                    className="flex-1"
                  >
                    İptal
                  </Button>
                  <Button
                    onClick={handleCloseTrade}
                    className="flex-1"
                  >
                    Ticareti Kapat
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

